import { jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import clsx from 'clsx';
import { useLazyInput } from '../hooks/use-lazy-input.js';
import { InputPrimitive } from '../input-primitive/input-primitive.js';
import { getVarName } from '../utils/css.js';
import { getFlags } from '../utils/flags.js';
import { columns, colspan, inputAlt, input, size, rootAlt, root, groupAlt, group } from './input.css.js';

const columnsVar = getVarName(columns);
const colspanVar = getVarName(colspan);
const _Input = forwardRef(({ className, size: size$1 = 'md', ...props }, ref) => (jsx(InputPrimitive, { "data-fpl-component": "", ...props, ref: ref, className: clsx(getFlags().fpl_input_style_update ? inputAlt : input, size[size$1], className) })));
_Input.displayName = 'Input';
/**
 * When an input should "contain" other elements, such as a hex input with a chit, or an input
 * with pills, `Input.Root` can be used as the container, and `Input.Field` as the typeable area.
 *
 * @compose { "root": true }
 * @see https://fpl.figma.design/components/inputs/input/
 */
const Root = forwardRef(({ size: size$1 = 'md', colspan, className, ...props }, ref) => {
    return (jsx(InputPrimitive.Root, { "data-fpl-component": "", ...props, ref: ref, className: clsx(getFlags().fpl_input_style_update ? rootAlt : root, size[size$1], className), style: { [colspanVar]: colspan } }));
});
Root.displayName = 'Input.Root';
/**
 * Group multiple inputs together such that they share focus and hover states,
 * and border radius of adjacent inputs is removed.
 *
 * @compose { "standalone": true, "strictChildren": true }
 */
const Group = forwardRef(({ size: size$1 = 'md', columns, ...props }, ref) => (jsx("div", { "data-fpl-component": "", ...props, ref: ref, className: clsx(getFlags().fpl_input_style_update ? groupAlt : group, size[size$1]), style: { [columnsVar]: columns } })));
Group.displayName = 'Input.Group';
/**
 * Make an input keep track of its own state for a short time while editing. Intended for controlled
 * inputs that may be slow to update, due to the state update triggering a big re-render.
 *
 * Lazy inputs should be preferred when the input does not need to modify the value in any way. If
 * the value needs to be modified (e.g. trimming whitespace), then the input should not use this
 * hook. Instead it can use a regular Input with a controlled value, or defer triggering onChange
 * to a later time, such as on blur.
 *
 * @note Uncontrolled inputs do not benefit from this as they don't trigger state updates as the
 *   user types.
 *
 * Must be used on a controlled input, so value and onChange are required.
 * @see https://fpl.figma.design/components/inputs/input/
 * @compose { "standalone": true }
 */
const Lazy = forwardRef((propsIn, ref) => {
    const props = useLazyInput(propsIn);
    return jsx(Input, { "data-fpl-component": "", ...props, ref: ref });
});
Lazy.displayName = 'Input.Lazy';
/**
 * A styled text input component.
 *
 * @see [Figma Design file](https://staging.figma.com/design/N0izGT6Y7WwEOJABqPXBCv/!-UI3-Library?node-id=790-34498)
 * @see https://fpl.figma.design/components/inputs/input/
 * @compose { "standalone": true }
 */
const Input = Object.assign(_Input, { Root, Group, Lazy });

export { Input };
//# sourceMappingURL=input.js.map
